import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsNative } from '~/context/common/DeviceContext';
import { useIsLoggedIn } from '~/context/common/UserContext';
import {
  selectIsMfeLoaded,
  selectIsMfeReady,
  selectMfeLoadCount,
  useMfeLoadStore
} from '~/state/common/MfeLoadState';

export const ACCOUNT_MODAL_MODULE = 'CoreAccountModal';

// Stable selectors — defined at module level for referential stability across renders.
// selectIsAccountModalReady is true only after ACCOUNT_MODAL_READY has fired (not merely 'loaded').
// ACCOUNT_MODAL_READY is the header MFE's signal that CoreAccountModal has fully
// initialised and is safe to receive openAccountModal calls.
export const selectIsAccountModalReady = selectIsMfeReady(ACCOUNT_MODAL_MODULE);
const selectIsAccountModalLoaded = selectIsMfeLoaded(ACCOUNT_MODAL_MODULE);
const selectAccountModalLoadCount = selectMfeLoadCount(ACCOUNT_MODAL_MODULE);

export default function useAccountModalReady() {
  const isLoggedIn = useIsLoggedIn();
  const isNative = useIsNative();
  const isReady = useMfeLoadStore(selectIsAccountModalReady);
  const accountModalLoaded = useMfeLoadStore(selectIsAccountModalLoaded);
  const accountModalLoadCount = useMfeLoadStore(selectAccountModalLoadCount);
  const pendingRef = useRef<(() => void) | null>(null);
  const [isPending, setIsPending] = useState(false);

  // Logged-in and native users never open the account modal, so they should
  // never wait for it. Only logged-out web users need deferred execution.
  const waitForAccountModal = !isLoggedIn && !isNative;

  useEffect(() => {
    if (isReady && pendingRef.current) {
      pendingRef.current();
      pendingRef.current = null;
      setIsPending(false);
    }
  }, [isReady]);

  const whenReady = useCallback(
    (action: () => void) => {
      if (!waitForAccountModal || isReady) {
        action();
      } else {
        datadogRum.addAction('vdp-purchase-cta-click-deferred', {
          accountModalLoaded,
          accountModalLoadCount
        });
        pendingRef.current = action;
        setIsPending(true);
      }
    },
    [accountModalLoadCount, accountModalLoaded, isReady, waitForAccountModal]
  );

  return { isPending: waitForAccountModal && isPending, isReady, whenReady };
}
